import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Plot from 'react-plotly.js';
import { API_ENDPOINTS } from '../../apiConfig';
import { generateSVICurve } from '../../utils/sviUtils';
import './VolCurve.css';

function VolCurve() {
  const [symbols, setSymbols] = useState([]);
  const [expiries, setExpiries] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('BTC');
  const [selectedExpiry, setSelectedExpiry] = useState('');
  const [chainData, setChainData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Track user's current zoom/pan state to preserve across updates
  const userZoomRef = useRef(null);
  // Track if this is initial load (to set default bounds)
  const isInitialLoadRef = useRef(true);

  // Fetch available symbols on mount
  useEffect(() => {
    fetch(API_ENDPOINTS.CRYPTO_SYMBOLS)
      .then(res => res.json())
      .then(data => {
        const symbolList = data.symbols || [];
        setSymbols(symbolList);
        if (symbolList.length > 0 && !symbolList.includes(selectedSymbol)) {
          setSelectedSymbol(symbolList[0]);
        }
      })
      .catch(err => console.error('Failed to fetch symbols:', err));
  }, []);

  // Fetch expiries when symbol changes
  useEffect(() => {
    if (!selectedSymbol) return;

    fetch(API_ENDPOINTS.CRYPTO_EXPIRIES(selectedSymbol))
      .then(res => res.json())
      .then(data => {
        const expList = data.expiries || [];
        setExpiries(expList);
        if (expList.length > 0) {
          if (!expList.includes(selectedExpiry)) {
            setSelectedExpiry(expList[0]);
          }
        } else {
          setSelectedExpiry('');
        }
      })
      .catch(err => console.error('Failed to fetch expiries:', err));
  }, [selectedSymbol]);

  // Reset zoom when symbol/expiry changes
  useEffect(() => {
    userZoomRef.current = null;
    isInitialLoadRef.current = true;
  }, [selectedSymbol, selectedExpiry]);

  // Fetch chain data
  const fetchChainData = useCallback(async () => {
    if (!selectedSymbol || !selectedExpiry) return;

    setLoading(true);
    setError(null);

    try {
      const url = `${API_ENDPOINTS.CRYPTO_OPTION_CHAIN}?symbol=${selectedSymbol}&expiry=${selectedExpiry}`;
      const res = await fetch(url);

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();
      setChainData(data);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch chain data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedSymbol, selectedExpiry]);

  // Initial fetch and polling
  useEffect(() => {
    fetchChainData();

    if (autoRefresh) {
      const interval = setInterval(fetchChainData, 5000);
      return () => clearInterval(interval);
    }
  }, [fetchChainData, autoRefresh]);

  // Generate smooth SVI curve (150 points) - only within strike bounds
  const sviCurveData = useMemo(() => {
    if (!chainData?.svi_params || !chainData.forward_price || !chainData.time_to_expiry) return [];
    if (!chainData?.strikes || chainData.strikes.length === 0) return [];

    // Calculate moneyness range from actual strikes
    const strikes = chainData.strikes;
    const forward = chainData.forward_price;
    const minStrike = Math.min(...strikes);
    const maxStrike = Math.max(...strikes);
    const minMoneyness = minStrike / forward;
    const maxMoneyness = maxStrike / forward;
    const moneynessRange = Math.max(maxMoneyness - 1, 1 - minMoneyness) * 1.05; // 5% margin

    const curvePoints = generateSVICurve(chainData, 150, moneynessRange);
    if (!curvePoints) return [];

    // Filter to only include points within strike bounds (with margin)
    const margin = (maxStrike - minStrike) * 0.05;
    return curvePoints
      .filter(p => p.strike >= minStrike - margin && p.strike <= maxStrike + margin)
      .map(p => ({
        strike: p.strike,
        fittedIV: p.fittedIV,
      }));
  }, [chainData]);

  // Raw IV scatter points (at original strikes)
  const rawIVData = useMemo(() => {
    if (!chainData?.strikes) return [];

    return chainData.strikes.map((strike, i) => ({
      strike,
      rawIV: chainData.raw_iv[i] ? parseFloat((chainData.raw_iv[i] * 100).toFixed(2)) : null,
    })).filter(d => d.rawIV !== null);
  }, [chainData]);

  // Calculate strike bounds with margin
  const strikeBounds = useMemo(() => {
    if (!chainData?.strikes || chainData.strikes.length === 0) {
      return null;
    }

    const strikes = chainData.strikes;
    const min = Math.min(...strikes);
    const max = Math.max(...strikes);
    const range = max - min;
    const margin = range * 0.05;

    return {
      min: min - margin,
      max: max + margin,
    };
  }, [chainData]);

  // Handle zoom/pan changes from user
  const handleRelayout = useCallback((e) => {
    // Capture user's zoom/pan state
    if (e['xaxis.range[0]'] !== undefined && e['xaxis.range[1]'] !== undefined) {
      userZoomRef.current = {
        xMin: e['xaxis.range[0]'],
        xMax: e['xaxis.range[1]'],
        yMin: e['yaxis.range[0]'],
        yMax: e['yaxis.range[1]'],
      };
      isInitialLoadRef.current = false;
    }
    // Handle autorange (reset zoom)
    if (e['xaxis.autorange'] || e['yaxis.autorange']) {
      userZoomRef.current = null;
    }
  }, []);

  // Plotly data traces
  const plotData = useMemo(() => {
    const traces = [];

    // Smooth fitted IV line
    if (sviCurveData.length > 0) {
      traces.push({
        x: sviCurveData.map(d => d.strike),
        y: sviCurveData.map(d => d.fittedIV),
        type: 'scatter',
        mode: 'lines',
        name: 'Fitted IV (SVI-Raw)',
        line: { color: '#10b981', width: 2 },
        hovertemplate: 'Strike: %{x:,.0f}<br>Fitted IV: %{y:.2f}%<extra></extra>',
      });
    }

    // Raw IV scatter points
    if (rawIVData.length > 0) {
      traces.push({
        x: rawIVData.map(d => d.strike),
        y: rawIVData.map(d => d.rawIV),
        type: 'scatter',
        mode: 'markers',
        name: 'Raw IV (Market)',
        marker: { color: '#3b82f6', size: 8, symbol: 'circle' },
        hovertemplate: 'Strike: %{x:,.0f}<br>Raw IV: %{y:.2f}%<extra></extra>',
      });
    }

    return traces;
  }, [sviCurveData, rawIVData]);

  // Plotly layout - preserve user zoom or use default bounds
  const plotLayout = useMemo(() => {
    // Determine X range: user zoom > default strike bounds
    let xRange;
    if (userZoomRef.current) {
      xRange = [userZoomRef.current.xMin, userZoomRef.current.xMax];
    } else if (strikeBounds) {
      xRange = [strikeBounds.min, strikeBounds.max];
    } else {
      xRange = undefined; // Let Plotly auto-range
    }

    // Determine Y range
    let yRange;
    if (userZoomRef.current && userZoomRef.current.yMin !== undefined) {
      yRange = [userZoomRef.current.yMin, userZoomRef.current.yMax];
    }

    return {
      title: null,
      autosize: true,
      height: 500,
      margin: { t: 40, r: 40, b: 60, l: 70 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      font: { color: '#a1a1aa' },
      // uirevision keeps UI state (zoom/pan) stable across data updates
      uirevision: `${selectedSymbol}-${selectedExpiry}`,
      xaxis: {
        title: 'Strike Price',
        range: xRange,
        gridcolor: '#27272a',
        tickformat: ',.0f',
        showgrid: true,
        zeroline: false,
      },
      yaxis: {
        title: 'Implied Volatility (%)',
        range: yRange,
        gridcolor: '#27272a',
        ticksuffix: '%',
        showgrid: true,
        zeroline: false,
      },
      legend: {
        orientation: 'h',
        yanchor: 'bottom',
        y: 1.02,
        xanchor: 'center',
        x: 0.5,
        bgcolor: 'rgba(0,0,0,0)',
      },
      hovermode: 'closest',
      dragmode: 'zoom',
      shapes: chainData?.forward_price ? [{
        type: 'line',
        x0: chainData.forward_price,
        x1: chainData.forward_price,
        y0: 0,
        y1: 1,
        yref: 'paper',
        line: { color: '#f59e0b', width: 1, dash: 'dash' },
      }] : [],
      annotations: chainData?.forward_price ? [{
        x: chainData.forward_price,
        y: 1,
        yref: 'paper',
        text: 'Forward',
        showarrow: false,
        font: { color: '#f59e0b', size: 11 },
        yshift: 10,
      }] : [],
    };
  }, [strikeBounds, chainData, selectedSymbol, selectedExpiry]);

  const plotConfig = {
    displayModeBar: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
    displaylogo: false,
    responsive: true,
    scrollZoom: true,
  };

  return (
    <div className="vol-curve">
      <div className="vol-curve-header">
        <h2>Implied Volatility Curve</h2>
        <div className="controls">
          <div className="control-group">
            <label>Symbol</label>
            <select
              value={selectedSymbol}
              onChange={e => setSelectedSymbol(e.target.value)}
            >
              {symbols.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <label>Expiry</label>
            <select
              value={selectedExpiry}
              onChange={e => setSelectedExpiry(e.target.value)}
              disabled={expiries.length === 0}
            >
              {expiries.map(e => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          </div>

          <label className="auto-refresh">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (5s)
          </label>

          <button
            onClick={fetchChainData}
            disabled={loading}
            className="refresh-btn"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>Error: {error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {chainData && (
        <div className="vol-curve-info">
          <div className="info-item">
            <span className="info-label">Forward</span>
            <span className="info-value">{chainData.forward_price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
          </div>
          <div className="info-item">
            <span className="info-label">ATM IV</span>
            <span className="info-value">
              {chainData.svi_params?.atm_iv
                ? (chainData.svi_params.atm_iv * 100).toFixed(2) + '%'
                : 'N/A'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Skew (rho)</span>
            <span className="info-value">
              {chainData.svi_params?.rho?.toFixed(4) || 'N/A'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Pin Strike</span>
            <span className="info-value">
              {chainData.svi_params?.pin_strike?.toLocaleString() || 'N/A'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Curvature</span>
            <span className="info-value">
              {chainData.svi_params?.sigma?.toFixed(4) || 'N/A'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">DTE</span>
            <span className="info-value">
              {chainData.time_to_expiry
                ? (chainData.time_to_expiry * 365).toFixed(1) + ' days'
                : 'N/A'}
            </span>
          </div>
          {lastUpdate && (
            <div className="info-item">
              <span className="info-label">Updated</span>
              <span className="info-value">{lastUpdate.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      )}

      <div className="chart-container">
        {loading && !chainData ? (
          <div className="loading">Loading vol curve data...</div>
        ) : plotData.length > 0 ? (
          <Plot
            data={plotData}
            layout={plotLayout}
            config={plotConfig}
            style={{ width: '100%', height: '500px' }}
            useResizeHandler={true}
            onRelayout={handleRelayout}
          />
        ) : (
          <div className="no-data">
            {selectedSymbol && selectedExpiry
              ? 'No data available for selected instrument'
              : 'Select a symbol and expiry to view the vol curve'}
          </div>
        )}
      </div>
    </div>
  );
}

export default VolCurve;
